import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { Link } from 'react-router-dom'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import { Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react'
import { getQueryVariable } from 'utils/query'
import routes from 'constants/routes'

const validationSchema = Yup.object().shape({
	password: Yup.string()
		.min(8, 'Password must be at least 8 characters')
		.matches(/[A-Z]/, 'Must contain at least one uppercase letter')
		.matches(/[0-9]/, 'Must contain at least one number')
		.matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, 'Must contain at least one special character')
		.required('Password is required'),
	passwordCopy: Yup.string()
		.oneOf([Yup.ref('password')], 'Passwords must match')
		.required('Please confirm your password'),
})

const ChangePasswordCN = () => {
	const [hasSubmitted, setHasSubmitted] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const token = getQueryVariable('token')

	if (hasSubmitted) {
		return (
			<div className="flex flex-col items-center text-center py-4">
				<div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
					<CheckCircle2 className="w-6 h-6 text-green-600" />
				</div>
				<h2 className="text-xl font-semibold text-[#074f9b] mb-2">Password Updated</h2>
				<p className="text-sm text-gray-500 mb-4">
					Your password has been successfully changed.
				</p>
				<Link
					to={routes.LOGIN}
					className="text-sm text-[#0080fe] hover:text-[#0073e6] no-underline font-medium"
				>
					Click here to login
				</Link>
			</div>
		)
	}

	return (
		<Formik
			initialValues={{ password: '', passwordCopy: '' }}
			validationSchema={validationSchema}
			onSubmit={async (values, { setSubmitting }) => {
				setErrorMessage('')
				if (!token) {
					setErrorMessage('Invalid or expired reset link. Please request a new one.')
					setSubmitting(false)
					return
				}
				try {
					const { data } = await axios.post('/api/changePasswords', {
						newPassword: values.password,
						token,
					})
					if (data.success) {
						setHasSubmitted(true)
					} else {
						setErrorMessage('Something went wrong. Please try again.')
					}
				} catch (e: any) {
					setErrorMessage(
						e.response?.data?.message || 'Something went wrong. Please try again.',
					)
				} finally {
					setSubmitting(false)
				}
			}}
		>
			{({ isSubmitting, handleChange, handleBlur, values, errors, touched }) => (
				<Form className="space-y-5">
					<h2 className="text-2xl font-semibold text-center text-[#074f9b]">
						Choose a New Password
					</h2>

					{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="cp-password">New Password</Label>
							<div className="relative">
								<Input
									id="cp-password"
									name="password"
									type={showPassword ? 'text' : 'password'}
									placeholder="Minimum 8 characters"
									value={values.password}
									onChange={handleChange}
									onBlur={handleBlur}
									className="h-11 pr-10"
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
								>
									{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
								</button>
							</div>
							{touched.password && errors.password && (
								<p className="text-xs text-red-500 mt-1">{errors.password}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="cp-passwordCopy">Confirm Password</Label>
							<Input
								id="cp-passwordCopy"
								name="passwordCopy"
								type={showPassword ? 'text' : 'password'}
								placeholder="Re-enter your password"
								value={values.passwordCopy}
								onChange={handleChange}
								onBlur={handleBlur}
								className="h-11"
							/>
							{touched.passwordCopy && errors.passwordCopy && (
								<p className="text-xs text-red-500 mt-1">{errors.passwordCopy}</p>
							)}
						</div>
					</div>

					<Button
						type="submit"
						isLarge
						disabled={isSubmitting}
						className="w-full"
					>
						{isSubmitting ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Updating...
							</>
						) : (
							'Reset My Password'
						)}
					</Button>
				</Form>
			)}
		</Formik>
	)
}

export default ChangePasswordCN
